import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from "recharts";

export default function BloodPressureChart() {
  const [readings, setReadings] = useState([]);
  const [range, setRange] = useState("9999"); // show All initially

  useEffect(() => {
    let unsubFS = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubFS) { unsubFS(); unsubFS = null; }
      if (!user) { setReadings([]); return; }

      const q = query(
        collection(db, "users", user.uid, "bloodPressure"),
        orderBy("takenAt", "asc")
      );

      unsubFS = onSnapshot(q, (snap) => {
        const data = snap.docs.map((doc) => {
          const d = doc.data();
          const taken = d.takenAt?.toDate ? d.takenAt.toDate() : null;
          return {
            systolic: Number(d.systolic || 0),
            diastolic: Number(d.diastolic || 0),
            pulse: Number(d.pulse || 0),
            period: d.period || "",
            takenAt: taken,
            dateLabel: taken ? taken.toLocaleDateString() : "",
          };
        });
        setReadings(data);
      }, (err) => {
        console.error("[BP chart] onSnapshot error:", err);
        setReadings([]);
      });
    });

    return () => {
      unsubAuth && unsubAuth();
      unsubFS && unsubFS();
    };
  }, []);

  const filtered = readings.filter((r) => {
    if (!r.takenAt) return false;
    const days = parseInt(range, 10);
    if (days >= 9999) return true;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return r.takenAt >= cutoff;
  });

  const exportCSV = () => {
    const headers = ["Date", "Systolic", "Diastolic", "Pulse", "Period"];
    const rows = filtered.map(r => [r.dateLabel, r.systolic, r.diastolic, r.pulse, r.period]);
    const csv = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const a = document.createElement("a");
    a.href = encodeURI(csv);
    a.download = "blood_pressure_readings.csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div style={{ width: "100%", height: 350, marginTop: "1rem" }}>
      <div style={{ marginBottom: "0.5rem" }}>
        <label>Range: </label>
        <select value={range} onChange={(e) => setRange(e.target.value)}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last 1 year</option>
          <option value="9999">All</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "1rem" }}>Export to CSV</button>
      </div>

      <ResponsiveContainer>
        <LineChart data={filtered.map(r => ({ ...r, dateTime: r.dateLabel }))}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="dateTime" />
          <YAxis />
          <Tooltip />
          <Legend />
          <ReferenceLine y={120} stroke="red" strokeDasharray="3 3" />
          <ReferenceLine y={80} stroke="blue" strokeDasharray="3 3" />
          <Line type="monotone" dataKey="systolic" stroke="red" dot />
          <Line type="monotone" dataKey="diastolic" stroke="blue" dot />
          <Line type="monotone" dataKey="pulse" stroke="green" dot />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
